import { getFoodById, type IMealFood } from './foods';
import type { MealFoodPlan, PlanDailyTargets } from './diet-plan-templates';

type Meal = 'breakfast' | 'lunch' | 'dinner' | 'snack';
const macros = (id: string, grams: number) => {
  const food = getFoodById(id)!;
  return [food.carbPer100g, food.proteinPer100g, food.fatPer100g].map(value => value * grams / 100);
};

// Food weights follow the existing database: oatmeal is dry, rice is cooked,
// meats/vegetables are edible ingredient weights. No raw-rice example is reused as cooked rice.
export function generateAttachedMeals(target: PlanDailyTargets, day: number): MealFoodPlan {
  const plan: Record<Meal, IMealFood[]> = { breakfast: [], lunch: [], dinner: [], snack: [] };
  const used = [0, 0, 0];
  function add(meal: Meal, id: string, grams: number) {
    const rounded = Math.max(0, Math.round(grams));
    if (!rounded) return;
    plan[meal].push({ foodId: id, grams: rounded });
    macros(id, rounded).forEach((value, index) => { used[index] += value; });
  }
  const vegetable = ['broccoli', 'spinach', 'lettuce'][Math.max(0, day - 1) % 3];
  // Fixed foods stay below the available daily macro budget for small profiles.
  const fixedScale = Math.min(1, target.carb / 140, target.protein / 70, target.fat / 40);
  add('lunch', vegetable, 250);
  add('dinner', 'cabbage', 250);
  add('breakfast', 'egg', 100 * fixedScale);
  add('breakfast', 'blueberry', 100 * fixedScale);
  add('snack', 'walnut', 15 * fixedScale);
  const staple: [Meal, string, number][] = [
    ['breakfast', 'oatmeal', 0.25], ['lunch', 'rice', 0.4],
    ['dinner', day % 2 ? 'sweet-potato' : 'purple-potato', 0.35],
  ];
  const proteins: [Meal, string][] = [['lunch', day % 2 ? 'chicken-breast' : 'cod'], ['dinner', 'beef']];
  const carbBudget = Math.max(0, target.carb - used[0]);
  staple.forEach(([meal, id, fraction]) => add(meal, id, carbBudget * fraction * 100 / getFoodById(id)!.carbPer100g));
  const proteinBudget = Math.max(0, target.protein - used[1]);
  const meatFat = proteins.reduce((sum, [, id]) => sum + getFoodById(id)!.fatPer100g / getFoodById(id)!.proteinPer100g / 2, 0);
  if (used[2] + proteinBudget * meatFat > target.fat) proteins[1][1] = 'cod';
  proteins.forEach(([meal, id]) => add(meal, id, proteinBudget * 50 / getFoodById(id)!.proteinPer100g));
  const oil = Math.max(0, target.fat - used[2]);
  add('lunch', 'olive-oil', oil / 2);
  add('dinner', 'olive-oil', oil / 2);
  // Rounding and the food database's own energy factors can produce small differences.
  // Every food is included in the displayed meal totals, rather than declaring the menu exact.
  return plan;
}
